import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Ticket,
  FileText,
  CreditCard,
  FileCheck,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Settings,
  Eye,
  EyeOff,
  RotateCcw,
  X
} from 'lucide-react';
import { useAuthStore } from '../context/authStore';
import { usePreferencesStore } from '../context/preferencesStore';
import { useFeatureFlag } from '../context/featureFlagStore';
import { useCompanyStore } from '../context/companyStore';
import CompanyFilter from '../components/common/CompanyFilter';
import { ticketsAPI, invoicesAPI, quotesAPI } from '../services/api';
import { useDisplay } from '../hooks/useDisplay';
import { formatMoney } from '../utils/format';

export default function DashboardPage() {
  const { user, can } = useAuthStore();
  const { selectedCompanyId } = useCompanyStore();
  // The customer's role decides what the dashboard shows and even fetches: an
  // End User sees their own tickets and nothing about money or quotes. With a
  // company selected, the role held at that company; otherwise any of them.
  const canInvoices = can('invoices', 'read', selectedCompanyId);
  const canPay = can('payments', 'read', selectedCompanyId);
  const canQuotes = can('quotes', 'read', selectedCompanyId);
  const canAssets = can('assets', 'read', selectedCompanyId);
  const canCustomize = useFeatureFlag('ui.dashboard_customization');

  // Quick action feature flags
  const showNewTicket = useFeatureFlag('ui.quick_action_new_ticket');
  const showMakePayment = useFeatureFlag('ui.quick_action_make_payment') && canPay;
  const showViewQuotes = useFeatureFlag('ui.quick_action_view_quotes') && canQuotes;
  const showViewAssets = useFeatureFlag('ui.quick_action_view_assets') && canAssets;
  const hasAnyQuickAction = showNewTicket || showMakePayment || showViewQuotes || showViewAssets;

  const {
    customerDashboardSections,
    toggleCustomerDashboardSection,
    resetCustomerDashboardToDefaults
  } = usePreferencesStore();
  const [showCustomizePanel, setShowCustomizePanel] = useState(false);
  const d = useDisplay();

  // Get section visibility
  const getSectionVisible = (id) => {
    const section = customerDashboardSections.find(s => s.id === id);
    return section?.visible !== false;
  };

  // Fetch summary data (with company filter for multi-company users)
  const { data: ticketsData } = useQuery({
    queryKey: ['tickets', 'summary', selectedCompanyId],
    // 100, not 5: the Open Tickets tile counts this list, and five rows capped it
    // at "5" for every company with more. Recent Tickets still shows the first five.
    queryFn: () => ticketsAPI.list({ pageSize: 100, companyId: selectedCompanyId }),
  });

  const { data: invoicesData } = useQuery({
    queryKey: ['invoices', 'open', selectedCompanyId],
    queryFn: () => invoicesAPI.getOpen({ companyId: selectedCompanyId }),
    enabled: canInvoices,
  });

  const { data: quotesData } = useQuery({
    queryKey: ['quotes', selectedCompanyId],
    queryFn: () => quotesAPI.list({ companyId: selectedCompanyId }),
    enabled: canQuotes,
  });

  const tickets = ticketsData?.data?.tickets || [];
  const openInvoices = invoicesData?.data?.invoices || [];
  const pendingQuotes = (quotesData?.data?.quotes || []).filter(q => q.status?.name === 'Open');
  
  // Calculate totals
  const totalDue = openInvoices.reduce((sum, inv) => sum + (inv.balance || 0), 0);
  
  return (
    <div className="animate-fade-in">
      {/* Welcome header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            Here's what's happening with your account.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CompanyFilter />
          {canCustomize && (
            <button
              onClick={() => setShowCustomizePanel(!showCustomizePanel)}
              className="btn-ghost btn-sm"
              title="Customize dashboard"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Customize Panel */}
      {showCustomizePanel && (
        <div className="mb-6 card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold text-neutral-900 dark:text-white">Customize Dashboard</h3>
            <button onClick={() => setShowCustomizePanel(false)} className="btn-ghost btn-sm">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="card-body">
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
              Toggle sections to show or hide them on your dashboard.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { id: 'stats', label: 'Stats Cards' },
                { id: 'recentTickets', label: 'Recent Tickets' },
                { id: 'openInvoices', label: 'Open Invoices' },
                { id: 'quickActions', label: 'Quick Actions' },
              ].map(section => (
                <button
                  key={section.id}
                  onClick={() => toggleCustomerDashboardSection(section.id)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    getSectionVisible(section.id)
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                      : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400'
                  }`}
                >
                  {getSectionVisible(section.id) ? (
                    <Eye className="w-3.5 h-3.5" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5" />
                  )}
                  {section.label}
                </button>
              ))}
            </div>
            <button
              onClick={resetCustomerDashboardToDefaults}
              className="text-sm text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 flex items-center gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
            </button>
          </div>
        </div>
      )}
      
      {/* Quick stats */}
      {getSectionVisible('stats') && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {d.visible('dashboard.stats.openTickets') && (
          <StatCard
            title={d.label('dashboard.stats.openTickets')}
            value={(() => { const n = tickets.filter((t) => !t.closedFlag).length; return n >= 100 ? '100+' : n; })()}
            icon={Ticket}
            href="/tickets"
            color="blue"
          />
          )}
          {can('tickets', 'work', selectedCompanyId) && tickets.some((t) => 'portalAssignee' in t) && (
          <StatCard
            title="Unassigned in your queue"
            value={tickets.filter((t) => !t.closedFlag && !t.withProvider && !t.portalAssignee && !t.owner?.id).length}
            icon={Ticket}
            href="/tickets"
            color="amber"
          />
          )}
          {canInvoices && d.visible('dashboard.stats.openInvoices') && (
          <StatCard
            title={d.label('dashboard.stats.openInvoices')}
            value={openInvoices.length}
            icon={FileText}
            href="/invoices"
            color="amber"
          />
          )}
          {canInvoices && d.visible('dashboard.stats.amountDue') && (
          <StatCard
            title={d.label('dashboard.stats.amountDue')}
            value={formatMoney(totalDue)}
            icon={CreditCard}
            href="/payments"
            color="green"
          />
          )}
          {canQuotes && d.visible('dashboard.stats.pendingQuotes') && (
          <StatCard
            title={d.label('dashboard.stats.pendingQuotes')}
            value={pendingQuotes.length}
            icon={FileCheck}
            href="/quotes"
            color="purple"
          />
          )}
        </div>
      )}
      
      {/* Recent activity sections */}
      <div className={`grid grid-cols-1 ${getSectionVisible('recentTickets') && canInvoices && getSectionVisible('openInvoices') ? 'lg:grid-cols-2' : ''} gap-6`}>
        {/* Recent Tickets */}
        {getSectionVisible('recentTickets') && d.visible('dashboard.section.recentTickets') && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold text-neutral-900 dark:text-white">{d.label('dashboard.section.recentTickets')}</h2>
            <Link to="/tickets" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="card-body p-0">
            {tickets.length === 0 ? (
              <div className="p-6 text-center text-neutral-500 dark:text-neutral-400">
                No tickets found
              </div>
            ) : (
              <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {tickets.slice(0, 5).map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="flex items-center gap-4 p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
                  >
                    <div className={`
                      w-10 h-10 rounded-lg flex items-center justify-center
                      ${ticket.closedFlag ? 'bg-green-100 dark:bg-green-900/50' : 'bg-amber-100 dark:bg-amber-900/50'}
                    `}>
                      {ticket.closedFlag ? (
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-900 dark:text-white truncate">
                        {ticket.summary}
                      </p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        #{ticket.id} • {ticket.status?.name || 'Open'}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Open Invoices */}
        {canInvoices && getSectionVisible('openInvoices') && d.visible('dashboard.section.openInvoices') && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold text-neutral-900 dark:text-white">{d.label('dashboard.section.openInvoices')}</h2>
            <Link to="/invoices" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="card-body p-0">
            {openInvoices.length === 0 ? (
              <div className="p-6 text-center text-neutral-500 dark:text-neutral-400">
                No open invoices
              </div>
            ) : (
              <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {openInvoices.slice(0, 5).map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">
                          Invoice #{invoice.invoiceNumber}
                        </p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          Due: {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-neutral-900 dark:text-white">
                        {formatMoney(invoice.balance || 0)}
                      </p>
                      <Link
                        to="/payments"
                        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      >
                        Pay now
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Quick actions */}
      {getSectionVisible('quickActions') && d.visible('dashboard.section.quickActions') && hasAnyQuickAction && (
      <div className="mt-8 card">
        <div className="card-header">
          <h2 className="font-semibold text-neutral-900 dark:text-white">{d.label('dashboard.section.quickActions')}</h2>
        </div>
        <div className="card-body">
          <div className="flex flex-wrap justify-evenly gap-4">
            {showNewTicket && (
              <QuickAction
                href="/tickets?new=true"
                icon={Ticket}
                label="New Ticket"
              />
            )}
            {showMakePayment && (
              <QuickAction
                href="/payments"
                icon={CreditCard}
                label="Make Payment"
              />
            )}
            {showViewQuotes && (
              <QuickAction
                href="/quotes"
                icon={FileCheck}
                label="View Quotes"
              />
            )}
            {showViewAssets && (
              <QuickAction
                href="/assets"
                icon={AlertCircle}
                label="View Assets"
              />
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, href, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  };

  return (
    <Link to={href} className="card p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">{title}</p>
          <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Link>
  );
}

function QuickAction({ href, icon: Icon, label }) {
  return (
    <Link
      to={href}
      className="flex-1 min-w-32 max-w-48 flex flex-col items-center gap-2 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 dark:hover:border-primary-700 transition-colors"
    >
      <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 text-center">{label}</span>
    </Link>
  );
}
